import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { processPersonalCyberCPR, transformVistaCPRData } from "../../../../api/cprServices";
import Input from "../../../../components/common/Input/Input";
import FileUploadInput from "../../../../components/common/newFileUploadInput/FileUploadInput";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../components/common/SingleDatePicker/SingleDatePicker";
import FormTabs from "../../../../components/common/tabs/formTabs";
import TelInput from "../../../../components/common/telInput/telInput";
import TravelDetailsTable from "../../../../components/common/TravelDetailsTable/TravelDetailsTable";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePersonalCyberFlow from "../../../../hooks/usePersonalCyberFlow";
import usePersonalCyberFormData from "../../../../hooks/usePersonalCyberFormData";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { selectPersonalCyberForm } from "../../../../redux/features/personalCyberFormSlice";
import {
  fetchProfileInfo,
  selectProfileData,
} from "../../../../redux/features/profileSlice";
import {
  clearPersonalCyberQuote,
  fetchPartyCode,
  fetchPersonalCyberQuote,
  selectPartyCode,
  selectPersonalCyberQuote,
  selectPersonalCyberQuoteError,
  selectPersonalCyberQuoteLoading,
} from "../../../../redux/features/quoteSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import {
  formatPremiumValue,
  formatPremiumWithCurrency,
} from "../../../../utils/index";
import TravelSummaryModal from "../../../newTravelPolicy/modals/travelSummaryModal";
import BottomStickyBtnLayout from "../../layouts/BottomStickyBtnLayout";
import PersonalCyberJourneyLayout from "../../layouts/PersonalCyberJourneyLayout";

// Gender tabs
const genderTabs = [
  { label_en: "Male", label_ar: "ذكر", type: "Male" },
  { label_en: "Female", label_ar: "أنثى", type: "Female" },
];

export const PERSONAL_CYBER_CONTACT_DETAILS_FORM =
  "PERSONAL_CYBER_CONTACT_DETAILS_FORM";

const PlanSelection = () => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const { handleNextSubstep } = usePersonalCyberFlow();
  const dispatch = useDispatch();
  const profileData = useSelector(selectProfileData);
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const personalCyberFormData = useSelector(selectPersonalCyberForm);
  const personalCyberQuote = useSelector(selectPersonalCyberQuote);
  const quoteLoading = useSelector(selectPersonalCyberQuoteLoading);
  const quoteError = useSelector(selectPersonalCyberQuoteError);
  const partyCode = useSelector(selectPartyCode);

  // Get current locale for number formatting
  const currentLocale = i18n.resolvedLanguage === "ar" ? "en-US" : "en-US";

  // Helper function for footer currency formatting with English numerals
  const formatFooterCurrency = (amount) => {
    if (i18n.resolvedLanguage === "ar") {
      // Arabic: use English numerals and reverse order (amount currency)
      return `${formatPremiumValue(amount, "en-US", 3)} د.ب`;
    } else {
      // English: standard format (currency amount)
      return formatPremiumWithCurrency(amount, "BHD", "en-US");
    }
  };

  // Determine if user is guest (same logic as travel insurance)
  const isGuest = !Boolean(loginTokenInfo);

  // Fetch profile data after login - more aggressive refetching
  useEffect(() => {
    console.log("Profile fetch effect triggered:", {
      isGuest,
      profileData: !!profileData,
      loginTokenInfo: !!loginTokenInfo,
    });

    if (!isGuest && loginTokenInfo) {
      // Always fetch profile data when user is logged in, even if we have cached data
      // This ensures fresh data after login from modal
      console.log("Fetching profile info for logged-in user");
      dispatch(fetchProfileInfo());
    }
  }, [isGuest, loginTokenInfo, dispatch]); // Removed profileData dependency to force refetch

  // Fetch party code for logged-in users
  useEffect(() => {
    if (!isGuest && profileData?.CPR && !partyCode) {
      console.log("Fetching party code for CPR:", profileData.CPR);
      dispatch(fetchPartyCode(profileData.CPR));
    }
  }, [isGuest, profileData, partyCode, dispatch]);

  // Nationality data from Redux
  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);

  // Fetch nationalities list on mount
  useEffect(() => {
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length, nationalitiesLoading]);

  // Handle login state change - force passenger recreation when user logs in
  useEffect(() => {
    const prevIsGuest =
      sessionStorage.getItem("wasGuestPlanSelection") === "true";
    const currentIsGuest = isGuest;

    console.log("Login state change check (planSelection):", {
      prevIsGuest,
      currentIsGuest,
      loginTokenInfo: !!loginTokenInfo,
      profileData: !!profileData,
    });

    // User just logged in (was guest, now not guest) and we have profile data
    if (prevIsGuest && !currentIsGuest && loginTokenInfo && profileData) {
      console.log(
        "User just logged in - recreating passenger with profile data"
      );

      // Force "For Myself" selection and recreate passenger with profile data
      setPolicyFor("myself");
      const loggedInPassenger = createLoggedInPassenger(1);
      setPassengers([loggedInPassenger]);

      // Clear errors and reset state
      setFieldErrors({});
      setHasValidationAttempt(false);
    }

    // Update the stored guest state
    sessionStorage.setItem("wasGuestPlanSelection", isGuest.toString());
  }, [isGuest, loginTokenInfo, profileData]); // Runs when login state or profile data changes

  // Personal cyber form data hooks
  const { savePersonalCyberDataInRedux, redux_form_data } =
    usePersonalCyberFormData(PERSONAL_CYBER_CONTACT_DETAILS_FORM, () => {});

  // Helper function to create default passenger
  const createDefaultPassenger = (id) => ({
    id,
    cprNumber: "",
    fullName: "",
    emailAddress: "",
    mobileNumber: "",
    nationality: "", // Empty for guest users - no default nationality
    gender: "", // No default gender selection
    dateOfBirth: {
      day: "",
      month: "",
      year: "",
      fullDate: "",
    },
    cprUploaded: false,
    cprFiles: [],
    // CPR processing state
    cprProcessing: false,
    cprData: null,
    cprProcessed: false,
  });

  const [passengers, setPassengers] = useState(() => [
    createDefaultPassenger(1),
  ]);

  // State for logged-in user selection
  const [policyFor, setPolicyFor] = useState(
    isGuest ? "someone_else" : "myself"
  );

  // State to track touched fields and form validation attempts
  const [touchedFields, setTouchedFields] = useState({});
  const [hasValidationAttempt, setHasValidationAttempt] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [hasCalledQuoteApi, setHasCalledQuoteApi] = useState(false);

  // Helper function to format profile date
  const formatProfileDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  // Helper function to parse profile date for passenger form
  const parseProfileDateForForm = (dateString) => {
    if (!dateString) return { day: "", month: "", year: "", fullDate: "" };
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear().toString();
      const fullDate = `${year}-${month}-${day}`;
      return {
        day,
        month,
        year,
        fullDate,
      };
    } catch {
      return { day: "", month: "", year: "", fullDate: "" };
    }
  };

  // Real logged-in user data from profile
  const loggedInUserData = {
    cprNumber: profileData?.CPR || "",
    fullName: profileData
      ? `${profileData.FirstName || ""} ${profileData.LastName || ""}`.trim()
      : "",
    nationality: profileData?.Nationality || "Bahraini",
    gender: profileData?.Gender === "M" ? "Male" : profileData?.Gender === "F" ? "Female" : "Male", // Convert M/F to Male/Female for display
    dateOfBirth: profileData?.DateOfBirth ? formatProfileDate(profileData.DateOfBirth) : "",
    email: profileData?.Email || "",
    mobile: profileData?.PhoneNumber || "",
  };

  // Helper function to get nationality code from string name
  const getNationalityCode = (nationalityString) => {
    if (!nationalityString) return null; // No default - guest must select

    // If it's already a number, return it
    if (typeof nationalityString === "number") return nationalityString;

    // Try to find the nationality in the options
    const found = nationalityOptions?.find(
      (opt) => opt.value?.toUpperCase() === nationalityString?.toUpperCase()
    );

    return found ? found.code : null; // No default if not found
  };

  // Helper function to get nationality name from code - Fixed to use 'label' field
  const getNationalityName = (nationalityCode) => {
    console.log("=== getNationalityName Debug (planSelection) ===");
    console.log(
      "Input nationalityCode:",
      nationalityCode,
      "Type:",
      typeof nationalityCode
    );
    console.log("nationalityOptions length:", nationalityOptions?.length);

    // Return the raw code if nationality options aren't loaded yet
    if (!nationalityOptions || nationalityOptions.length === 0) {
      console.log(
        "Nationality options not loaded yet, returning raw code:",
        nationalityCode
      );
      return nationalityCode; // Return raw code, don't guess
    }

    if (!nationalityCode && nationalityCode !== 0) {
      console.log("No nationality code provided, returning null");
      return null;
    }

    // If it's already a string and not a number-like string, return it
    if (typeof nationalityCode === "string" && isNaN(nationalityCode)) {
      console.log(
        "Nationality code is already a string name:",
        nationalityCode
      );
      return nationalityCode;
    }

    // Convert string numbers to actual numbers for lookup
    const codeToLookup =
      typeof nationalityCode === "string"
        ? parseInt(nationalityCode)
        : nationalityCode;
    console.log("Code to lookup:", codeToLookup);

    // Find the nationality name by code - exact match on code field
    const found = nationalityOptions.find((opt) => opt.code === codeToLookup);
    console.log("Found nationality object:", found);

    if (found) {
      // Use 'label' field as primary, 'value' as fallback
      const result = found.label || found.value;
      console.log("Successfully found nationality:", result);
      console.log("=== End getNationalityName Debug (planSelection) ===");
      return result;
    }

    console.log("No nationality found for code:", codeToLookup);
    console.log("=== End getNationalityName Debug (planSelection) ===");
    return nationalityCode; // Return original code if lookup fails
  };

  // Helper function to get nationality name in Arabic
  const getNationalityInArabic = (englishName) => {
    const nationalityTranslations = {
      bahraini: "بحريني",
      saudi: "سعودي",
      emirati: "إماراتي",
      kuwaiti: "كويتي",
      qatari: "قطري",
      omani: "عماني",
      egyptian: "مصري",
      jordanian: "أردني",
      lebanese: "لبناني",
      syrian: "سوري",
      iraqi: "عراقي",
      palestinian: "فلسطيني",
      yemeni: "يمني",
      sudanese: "سوداني",
      moroccan: "مغربي",
      tunisian: "تونسي",
      algerian: "جزائري",
      libyan: "ليبي",
      indian: "هندي",
      pakistani: "باكستاني",
      bangladeshi: "بنغلاديشي",
      filipino: "فلبيني",
      indonesian: "إندونيسي",
      "sri lankan": "سريلانكي",
      nepalese: "نيبالي",
      american: "أمريكي",
      british: "بريطاني",
      canadian: "كندي",
      australian: "أسترالي",
      german: "ألماني",
      french: "فرنسي",
      italian: "إيطالي",
      spanish: "إسباني",
      russian: "روسي",
      chinese: "صيني",
      japanese: "ياباني",
      korean: "كوري",
      turkish: "تركي",
      iranian: "إيراني",
    };

    // Make case-insensitive lookup
    const lowerName = englishName?.toLowerCase();
    return nationalityTranslations[lowerName] || englishName;
  };

  // Helper function to create passenger from logged-in user data
  const createLoggedInPassenger = (id) => {
    const passenger = {
      id,
      cprNumber: loggedInUserData.cprNumber,
      fullName: loggedInUserData.fullName,
      emailAddress: loggedInUserData.email,
      mobileNumber: loggedInUserData.mobile,
      nationality: getNationalityCode(loggedInUserData.nationality) || 14, // Default to Bahraini for logged-in users only
      gender: loggedInUserData.gender,
      dateOfBirth: parseProfileDateForForm(profileData?.DateOfBirth),
      cprUploaded: true, // Assume logged-in user has verified CPR
      cprFiles: [], // Empty files for logged-in user
      passengerType: "primary", // Mark as primary passenger
    };

    return passenger;
  };

  // Build travel data for displaying logged-in user info
  const buildLoggedInUserTravelData = () => {
    const nationalityName = getNationalityName(loggedInUserData.nationality);

    const data = {
      [translate("CPR Number", "رقم الهوية الوطنية")]:
        loggedInUserData.cprNumber,
      [translate("Full Name", "الاسم الكامل")]: loggedInUserData.fullName,
      [translate("Nationality", "الجنسية")]: translate(
        nationalityName,
        getNationalityInArabic(nationalityName)
      ),
      [translate("Gender", "الجنس")]: translate(loggedInUserData.gender, "ذكر"),
    };

    // Always show Date of Birth field, empty if no data
    data[translate("Date of Birth", "تاريخ الميلاد")] =
      loggedInUserData.dateOfBirth || "";

    return data;
  };

  // Handle policy selection change
  const handlePolicyForChange = (selection) => {
    if (selection !== policyFor) userToggledPolicyRef.current = true;
    setPolicyFor(selection);

    if (selection === "myself" && !isGuest) {
      // Set logged-in user as primary passenger
      const loggedInPassenger = createLoggedInPassenger(1);
      setPassengers([loggedInPassenger]);
    } else {
      // Reset to default passengers for "someone else" or guest
      setPassengers([createDefaultPassenger(1)]);
    }

    // Clear field errors when changing selection
    setFieldErrors({});
  };

  // Initialize passengers based on saved data - responsive to login state and profile data
  useEffect(() => {
    console.log("Passengers initialization effect:", {
      redux_form_data: !!redux_form_data,
      isGuest,
      profileData: !!profileData,
      loginTokenInfo: !!loginTokenInfo,
    });

    // Restore saved contact details if available
    if (
      redux_form_data &&
      redux_form_data.passengers &&
      redux_form_data.passengers.length > 0
    ) {
      console.log(
        "Restoring saved passengers from redux:",
        redux_form_data.passengers
      );
      setPassengers(redux_form_data.passengers);
      setPolicyFor(
        redux_form_data.policyFor || (isGuest ? "someone_else" : "myself")
      );
      return; // Exit early to prevent further initialization
    } else {
      // Initialize with defaults
      console.log("Initializing default passengers");
      setPolicyFor(isGuest ? "someone_else" : "myself");

      if (!isGuest && profileData) {
        console.log("Creating logged-in passenger with profile data");
        const loggedInPassenger = createLoggedInPassenger(1);
        setPassengers([loggedInPassenger]);
      } else {
        console.log("Creating default passenger");
        const defaultPassenger = createDefaultPassenger(1);

        // For guests, prefill the primary passenger from the start-page
        // basic info so the customer info captured at the start carries
        // through into contactDetails (and into the quote payload).
        if (isGuest) {
          const basicInfo =
            personalCyberFormData?.PERSONAL_CYBER_BASIC_INFO_FORM || {};
          if (
            basicInfo.fullName ||
            basicInfo.email ||
            basicInfo.contactNumber ||
            basicInfo.birthDate
          ) {
            const [year = "", month = "", day = ""] = (
              basicInfo.birthDate || ""
            ).split("-");
            defaultPassenger.fullName =
              basicInfo.fullName || defaultPassenger.fullName;
            defaultPassenger.emailAddress =
              basicInfo.email || defaultPassenger.emailAddress;
            defaultPassenger.mobileNumber =
              basicInfo.contactNumber || defaultPassenger.mobileNumber;
            defaultPassenger.cprNumber =
              basicInfo.cpr || defaultPassenger.cprNumber;
            if (basicInfo.birthDate) {
              defaultPassenger.dateOfBirth = {
                day,
                month,
                year,
                fullDate: basicInfo.birthDate,
              };
            }
          }
        }

        setPassengers([defaultPassenger]);
      }
    }
  }, [redux_form_data, isGuest, profileData, loginTokenInfo, personalCyberFormData]); // Added profileData and loginTokenInfo dependencies

  // Note: we intentionally do NOT auto-mark hasCalledQuoteApi when a Redux
  // quote exists. The plan-picker writes a preview quote to Redux (so the
  // price stays visible on entry), but a fresh quote with the real passenger
  // CPR/details must still be fired once the form is fully valid.

  // Auto-call quote API when form data is complete
  useEffect(() => {
    // Check if we have all required data to call quote API
    const sumInsuredData =
      personalCyberFormData?.PERSONAL_CYBER_SUM_INSURED_FORM;

    if (
      !sumInsuredData?.selectedSumInsured ||
      passengers.length === 0 ||
      hasCalledQuoteApi
    ) {
      return; // Don't call API yet
    }

    const passenger = passengers[0];

    // Check if passenger has all required fields
    const hasAllFields =
      passenger.fullName &&
      passenger.cprNumber &&
      passenger.emailAddress &&
      passenger.mobileNumber;

    if (!hasAllFields) {
      return; // Don't call API yet
    }

    // For logged-in users, auto-call quote API ONLY if partyCode is available
    if (!isGuest && policyFor === "myself") {
      if (!partyCode) {
        console.log("⏳ Waiting for party code before auto-calling quote API...");
        return; // Don't call API yet, wait for partyCode
      }
      console.log("Auto-calling quote API for logged-in user with partyCode:", partyCode);
      callQuoteAPI();
      return; // Exit early after calling for "myself"
    }
  }, [
    passengers,
    personalCyberFormData,
    isGuest,
    policyFor,
    hasCalledQuoteApi,
    partyCode, // Add partyCode as dependency
  ]);

  // Function to call quote API
  const callQuoteAPI = async () => {
    if (quoteLoading || hasCalledQuoteApi) return;

    const contactDetailsData = {
      passengers,
      policyFor,
    };

    const completeFormData = {
      ...personalCyberFormData,
      PERSONAL_CYBER_CONTACT_DETAILS_FORM: contactDetailsData,
    };

    try {
      console.log("=== Auto-calling Personal Cyber Quote API ===");
      console.log("Complete form data:", completeFormData);

      const quoteAction = await dispatch(
        fetchPersonalCyberQuote(completeFormData)
      );
      const quoteResult = unwrapResult(quoteAction);

      if (
        quoteResult.result?.success &&
        quoteResult.result?.data?.totalPremium
      ) {
        console.log("✅ Quote API successful (auto-called)");
        setHasCalledQuoteApi(true);
      } else {
        console.log("Quote API returned error:", quoteResult.result?.error);
        setHasCalledQuoteApi(true); // Mark as called to prevent further auto-retry
        // Don't show error toast for auto-call, let user manually trigger
      }
    } catch (error) {
      console.error("Quote API exception (auto-call):", error);
    }
  };

  // Reset quote API flag and clear the displayed quote ONLY when the user
  // actually toggles policy type (not when policyFor is set programmatically
  // during init/restore). Programmatic sets must keep the plan-picker preview
  // quote intact so the price stays visible on entry.
  const userToggledPolicyRef = useRef(false);
  useEffect(() => {
    if (!userToggledPolicyRef.current) return;
    userToggledPolicyRef.current = false;
    setHasCalledQuoteApi(false);
    dispatch(clearPersonalCyberQuote());
  }, [policyFor]);

  // Auto-call quote API when Someone Else data is complete and valid
  useEffect(() => {
    // Only auto-call for "someone else" when data is complete
    if (policyFor !== "someone_else" || hasCalledQuoteApi || quoteLoading) {
      return;
    }

    // Check if passenger data is complete and valid
    if (passengers.length === 0) return;

    const passenger = passengers[0];

    // Check all required fields are filled
    const isDataComplete =
      passenger.cprNumber?.trim() &&
      passenger.cprNumber.length === 9 &&
      passenger.fullName?.trim() &&
      /^[a-zA-Z\u0600-\u06FF\s\.]+$/.test(passenger.fullName) &&
      passenger.emailAddress?.trim() &&
      /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(passenger.emailAddress) &&
      passenger.mobileNumber?.trim() &&
      passenger.mobileNumber.length === 8 &&
      passenger.nationality &&
      passenger.gender &&
      passenger.dateOfBirth?.fullDate?.trim() &&
      passenger.cprFiles?.length > 0;

    if (!isDataComplete) {
      console.log("Data incomplete - not calling quote API");
      return;
    }

    // Extra check for "someone_else": Ensure nationality is a valid number code (not text)
    if (typeof passenger.nationality !== 'number' && isNaN(Number(passenger.nationality))) {
      console.log("Nationality is not a valid code, not calling quote API:", passenger.nationality);
      return;
    }

    // Validate date of birth (not in future, age >= 18)
    const birthDate = new Date(passenger.dateOfBirth.fullDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (birthDate > today) return; // Future date

    const age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const isBeforeBirthday =
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDate.getDate());
    const actualAge = isBeforeBirthday ? age - 1 : age;

    if (actualAge < 18) return; // Under 18

    // All validation passed - call quote API
    console.log("All fields valid for Someone Else - auto-calling quote API");
    callQuoteAPI();
  }, [passengers, policyFor, hasCalledQuoteApi, quoteLoading]);

  // Helper function to get field error for a specific passenger
  const getFieldError = (passengerIndex, field) => {
    const fieldKey = `${field}_${passengerIndex}`;
    return fieldErrors[fieldKey] || "";
  };

  // Custom validation function with proper priority
  const validatePassengerFields = (passenger) => {
    const newErrors = {};

    // CPR validation - required first, then format
    if (!passenger.cprNumber?.trim()) {
      newErrors.cprNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (passenger.cprNumber.length !== 9) {
      newErrors.cprNumber = translate(
        "CPR number must be 9 digits.",
        "يجب أن يتكون رقم الهوية الوطنية من 9 أرقام."
      );
    }

    // Full name validation - required first, then format
    if (!passenger.fullName?.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[a-zA-Z\u0600-\u06FF\s\.]+$/.test(passenger.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
      );
    }

    // Email validation - required first, then format
    if (!passenger.emailAddress?.trim()) {
      newErrors.emailAddress = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (
      !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(passenger.emailAddress)
    ) {
      newErrors.emailAddress = translate(
        "Invalid email address.",
        "البريد الإلكتروني غير صحيح."
      );
    }

    // Mobile validation - required first, then length and numeric check
    if (!passenger.mobileNumber?.trim()) {
      newErrors.mobileNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (
      !/^\d+$/.test(passenger.mobileNumber) ||
      passenger.mobileNumber.length !== 8
    ) {
      newErrors.mobileNumber = translate(
        "Invalid mobile number.",
        "رقم الهاتف المحمول غير صحيح."
      );
    }

    // Nationality validation - required only (can be number or string)
    if (!passenger.nationality && passenger.nationality !== 0) {
      newErrors.nationality = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Gender validation - required only
    if (!passenger.gender?.trim()) {
      newErrors.gender = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Date of birth validation - check if date is provided
    if (!passenger.dateOfBirth.fullDate?.trim()) {
      newErrors["dateOfBirth.day"] = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Date of birth future validation
      const birthDate = new Date(passenger.dateOfBirth.fullDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (birthDate > today) {
        newErrors["dateOfBirth.day"] = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
        );
      } else {
        // Check if user is at least 18 years old
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDifference = today.getMonth() - birthDate.getMonth();

        const isBeforeBirthday =
          monthDifference < 0 ||
          (monthDifference === 0 && today.getDate() < birthDate.getDate());

        const actualAge = isBeforeBirthday ? age - 1 : age;

        if (actualAge < 18) {
          newErrors["dateOfBirth.day"] = translate(
            "You must be at least 18 years old.",
            "يجب أن تكون 18 عامًا على الأقل."
          );
        }
      }
    }

    return newErrors;
  };

  const handleInputChange = (passengerIndex, field, value) => {
    let processedValue = value;

    // Apply input formatting based on field type
    if (field === "fullName") {
      // Full Name: Allow all characters including numbers and special characters
      processedValue = value;
    }

    setPassengers((prevPassengers) => {
      const updatedPassengers = [...prevPassengers];
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [parentField]: {
            ...updatedPassengers[passengerIndex][parentField],
            [childField]: processedValue,
          },
        };
      } else {
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [field]: processedValue,
        };
      }

      return updatedPassengers;
    });

    // Validate field after state update
    setTimeout(() => {
      const updatedPassengers = [...passengers];
      if (field.includes(".")) {
        const [parentField, childField] = field.split(".");
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [parentField]: {
            ...updatedPassengers[passengerIndex][parentField],
            [childField]: processedValue,
          },
        };
      } else {
        updatedPassengers[passengerIndex] = {
          ...updatedPassengers[passengerIndex],
          [field]: processedValue,
        };
      }

      const updatedPassenger = updatedPassengers[passengerIndex];
      const passengerErrors = validatePassengerFields(updatedPassenger);

      // Update field errors - handle both simple and nested fields
      setFieldErrors((prevErrors) => {
        const newErrors = { ...prevErrors };

        // For dateOfBirth object, set the error for dateOfBirth.day
        if (field === "dateOfBirth") {
          const dobFieldKey = `dateOfBirth.day_${passengerIndex}`;
          newErrors[dobFieldKey] = passengerErrors["dateOfBirth.day"] || "";
        } else {
          // For other fields, use the field name directly
          const fieldKey = `${field}_${passengerIndex}`;
          newErrors[fieldKey] = passengerErrors[field] || "";
        }

        return newErrors;
      });
    }, 0);
  };

  // Mobile date picker handler
  const handleMobileDatePickerSelect = (passengerIndex, dateString) => {
    if (!dateString) return;

    // Parse the date string (YYYY-MM-DD format from mobile date picker)
    const [year, month, day] = dateString.split("-");

    handleInputChange(passengerIndex, "dateOfBirth", {
      day: day,
      month: month,
      year: year,
      fullDate: dateString,
    });
  };

  const handleFileSelect = async (fileData, passengerIndex) => {
    // Mark CPR field as touched for this passenger
    setTouchedFields((prev) => ({
      ...prev,
      [`cpr_${passengerIndex}`]: true,
    }));

    // Update passenger with uploaded file
    setPassengers((prevPassengers) => {
      const updatedPassengers = [...prevPassengers];
      const currentFiles = updatedPassengers[passengerIndex].cprFiles || [];

      // Add new file to the array
      updatedPassengers[passengerIndex] = {
        ...updatedPassengers[passengerIndex],
        cprFiles: [...currentFiles, fileData],
        cprUploaded: true,
        cprProcessing: true, // Start processing state
      };

      return updatedPassengers;
    });

    // Clear any field errors when files are uploaded
    setFieldErrors((prevErrors) => ({
      ...prevErrors,
      [`cpr_${passengerIndex}`]: "",
    }));

    // Process CPR with OCR in background
    if (fileData && fileData.file) {
      try {
        console.log(
          "Starting CPR processing for passenger",
          passengerIndex + 1
        );

        const result = await processPersonalCyberCPR(fileData.file);

        if (result.success && result.data?.output?.status) {
          // CPR processing successful
          console.log(
            "CPR processing completed successfully for passenger",
            passengerIndex + 1,
            result.data
          );

          const cprData = transformVistaCPRData(result.data);

          // Auto-fill passenger data with CPR information
          setPassengers((prevPassengers) => {
            const updatedPassengers = [...prevPassengers];
            updatedPassengers[passengerIndex] = {
              ...updatedPassengers[passengerIndex],
              // Auto-fill fields from CPR data
              cprNumber:
                cprData.cprNumber ||
                updatedPassengers[passengerIndex].cprNumber,
              fullName:
                cprData.fullName || updatedPassengers[passengerIndex].fullName,
              dateOfBirth: cprData.dateOfBirth
                ? {
                    ...cprData.dateOfBirth,
                    fullDate:
                      cprData.dateOfBirth.day &&
                      cprData.dateOfBirth.month &&
                      cprData.dateOfBirth.year
                        ? `${
                            cprData.dateOfBirth.year
                          }-${cprData.dateOfBirth.month.padStart(
                            2,
                            "0"
                          )}-${cprData.dateOfBirth.day.padStart(2, "0")}`
                        : "",
                  }
                : updatedPassengers[passengerIndex].dateOfBirth,
              gender:
                cprData.gender || updatedPassengers[passengerIndex].gender,
              nationality:
                cprData.nationality ||
                updatedPassengers[passengerIndex].nationality,
              // Update processing state
              cprProcessing: false,
              cprData: cprData,
              cprProcessed: true,
            };
            return updatedPassengers;
          });

          toast.success(
            translate(
              "CPR processed successfully!",
              "تم معالجة بطاقة الهوية بنجاح!"
            )
          );
        } else {
          // CPR processing failed
          console.warn(
            "CPR processing failed for passenger",
            passengerIndex + 1,
            result.error || result.data
          );

          setPassengers((prevPassengers) => {
            const updatedPassengers = [...prevPassengers];
            updatedPassengers[passengerIndex] = {
              ...updatedPassengers[passengerIndex],
              cprProcessing: false,
            };
            return updatedPassengers;
          });

          // Show error toast for consistency with other flows
          toast.error(
            translate(
              "Failed to process CPR. Please fill the form manually.",
              "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
            )
          );
        }
      } catch (error) {
        console.error(
          "CPR processing error for passenger",
          passengerIndex + 1,
          error
        );

        setPassengers((prevPassengers) => {
          const updatedPassengers = [...prevPassengers];
          updatedPassengers[passengerIndex] = {
            ...updatedPassengers[passengerIndex],
            cprProcessing: false,
          };
          return updatedPassengers;
        });

        // Show error toast for consistency with other flows
        toast.error(
          translate(
            "Failed to process CPR. Please fill the form manually.",
            "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
          )
        );
      }
    }
  };

  const handleFileRemove = (passengerIndex) => {
    // Mark CPR field as touched for this passenger
    setTouchedFields((prev) => ({
      ...prev,
      [`cpr_${passengerIndex}`]: true,
    }));

    // Clear CPR data and processing state
    setPassengers((prevPassengers) => {
      const updatedPassengers = [...prevPassengers];
      updatedPassengers[passengerIndex] = {
        ...updatedPassengers[passengerIndex],
        cprUploaded: false,
        cprFiles: [],
        cprProcessing: false,
        cprData: null,
        cprProcessed: false,
        // Don't clear form data - let user decide if they want to keep it
      };
      return updatedPassengers;
    });
  };

  // Function to check if CPR files are uploaded for a specific passenger
  const getCprUploadError = (passengerIndex) => {
    const passenger = passengers[passengerIndex];

    // Skip CPR validation for logged-in user when "For Myself" is selected
    // (first passenger is the logged-in user and already authenticated)
    if (!isGuest && policyFor === "myself" && passengerIndex === 0) {
      return "";
    }

    // Only show error if field has been touched OR validation has been attempted
    const fieldTouched = touchedFields[`cpr_${passengerIndex}`];
    if (!fieldTouched && !hasValidationAttempt) {
      return "";
    }

    if (!passenger.cprFiles || passenger.cprFiles.length === 0) {
      return translate("This field is required.", "هذا الحقل مطلوب.");
    }
    return "";
  };

  const handleContinue = async () => {
    // Mark that validation has been attempted
    setHasValidationAttempt(true);

    let allValid = true;
    const newFieldErrors = {};

    // Validate each passenger's fields
    for (let i = 0; i < passengers.length; i++) {
      const passenger = passengers[i];

      // Skip validation for logged-in user when "For Myself" is selected
      if (!isGuest && policyFor === "myself" && i === 0) {
        continue;
      }

      // Validate passenger fields and collect errors
      const passengerErrors = validatePassengerFields(passenger);

      // Add passenger-specific field errors
      Object.keys(passengerErrors).forEach((field) => {
        const fieldKey = `${field}_${i}`;
        newFieldErrors[fieldKey] = passengerErrors[field];
        allValid = false;
      });

      // Check CPR upload requirement (skip for logged-in user when "For Myself")
      if (!passenger.cprFiles || passenger.cprFiles.length === 0) {
        allValid = false;
      }
    }

    // Update field errors state to show validation messages
    setFieldErrors(newFieldErrors);

    if (allValid) {
      // Save contact details data to Redux first
      const contactDetailsData = {
        passengers,
        policyFor,
      };

      await savePersonalCyberDataInRedux(contactDetailsData);

      // If quote was already called, check if it was successful before proceeding
      if (hasCalledQuoteApi) {
        if (
          personalCyberQuote?.totalPremium &&
          personalCyberQuote.totalPremium > 0
        ) {
          console.log(
            "Using existing successful quote, proceeding to next step"
          );
          handleNextSubstep();
          return;
        } else {
          console.log("Previous quote attempt failed, showing error");
          toast.error(
            translate(
              "Quote generation failed. Please try again or contact support.",
              "فشل إنشاء التسعيرة. يرجى المحاولة مرة أخرى أو الاتصال بالدعم."
            )
          );
          return;
        }
      }

      // Build complete form data for quote API call
      // We need to manually build this because Redux might not have updated yet
      const completeFormData = {
        ...personalCyberFormData,
        PERSONAL_CYBER_CONTACT_DETAILS_FORM: contactDetailsData,
      };

      // Call the quote API if not already called
      try {
        console.log("=== Calling Personal Cyber Quote API (on Continue) ===");
        console.log("Complete form data:", completeFormData);

        const quoteAction = await dispatch(
          fetchPersonalCyberQuote(completeFormData)
        );
        const quoteResult = unwrapResult(quoteAction);

        console.log("Quote API result:", quoteResult);

        if (
          quoteResult.result?.success &&
          quoteResult.result?.data?.totalPremium
        ) {
          console.log(
            "✅ Personal Cyber Quote successful:",
            quoteResult.result.data
          );
          setHasCalledQuoteApi(true);
          toast.success(
            translate(
              "Quote generated successfully!",
              "تم إنشاء التسعيرة بنجاح!"
            )
          );
          // Proceed to next step only if we have valid pricing data
          handleNextSubstep();
        } else {
          console.error("❌ Quote API failed:", quoteResult.result?.error);
          setHasCalledQuoteApi(true); // Mark as called to prevent auto-retry
          toast.error(
            translate(
              "Unable to generate quote. Please check your information and try again.",
              "غير قادر على إنشاء التسعيرة. يرجى التحقق من معلوماتك والمحاولة مرة أخرى."
            )
          );
          // DO NOT proceed to next step - block the user here
        }
      } catch (error) {
        console.error("Quote API exception:", error);
        toast.error(
          translate(
            "Error getting quote. Please try again.",
            "خطأ في الحصول على التسعيرة. يرجى المحاولة مرة أخرى."
          )
        );
      }
    }
  };
  const renderLoggedInSection = () => {
    if (isGuest) return null;

    // Show loading state if profile data is not available yet
    if (!profileData) {
      return (
        <div className="mb-6">
          <div className="bg-[#E3F8FF] rounded-lg p-4 mb-4">
            <div className="animate-pulse [animation-duration:3s]">
              <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      );
    }

    const userName = loggedInUserData.fullName || translate("User", "المستخدم");

    return (
      <div className="mb-4 mt-8 max767:px-5">
        <p className="text-[#48535B] text-md font-normal mb-2">
          {translate(
            <>
              You're logged in as <span className="font-bold">{userName}</span>. Choose whether to
              issue this policy under your name or someone else's.
            </>,
            <>
              أنت مسجل الدخول كـ <span className="font-bold">{userName}</span>. اختر ما إذا كنت
              تريد إصدار هذه الوثيقة باسمك أو باسم شخص آخر.
            </>
          )}
        </p>

        {/* Policy For Selection Tabs */}
        <div className={`${quoteLoading ? "opacity-50 pointer-events-none cursor-not-allowed" : ""}`}>
          <FormTabs
            label=""
            tabs={[
              {
                type: "myself",
                label_en: "For Myself",
                label_ar: "لنفسي",
              },
              {
                type: "someone_else",
                label_en: "For Someone Else",
                label_ar: "لشخص آخر",
              },
            ]}
            activeTab={policyFor}
            onTabClick={handlePolicyForChange}
          />
        </div>

        {/* Show logged-in user info table when "For Myself" is selected */}
        {policyFor === "myself" && (
          <div className="mt-6">
            {/* User Info Table - Clean design without headers */}
            <div className="mb-6">
              {profileData ? (
                <TravelDetailsTable
                  travelData={buildLoggedInUserTravelData()}
                  className="!bg-[#F6F8F9] max767:bg-white !border-none max767:!border max767:border-[#E5E7EB] rounded-[16px] p-4 max767:p-0 overflow-hidden"
                  innerClassName="border border-[#E5E7EB] rounded-[10px]"
                />
              ) : (
                <div className="animate-pulse [animation-duration:3s] space-y-3">
                  <div className="h-4 bg-gray-300 rounded w-full"></div>
                  <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                  <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPassengerForm = (passenger, index) => {
    return (
      <div key={passenger.id} className="mb-4 max767:px-5">
        {/* No headers needed - personal cyber insurance doesn't use passenger terminology */}

        {/* CPR Upload */}
        <div className="mb-3">
          <label className="mb-1 text-[#48535B] block text-base">
            {translate(
              "Upload CPR (front & back)",
              "تحميل الهوية الوطنية (من الأمام والخلف)"
            )}
          </label>
          <FileUploadInput
            name={`cpr_passenger_${passenger.id}`}
            onFileSelect={(fileData) => handleFileSelect(fileData, index)}
            onFileRemove={() => handleFileRemove(index)}
            onLoseFocus={() =>
              setTouchedFields((prev) => ({ ...prev, [`cpr_${index}`]: true }))
            }
            validTypes={["image/jpeg", "image/png", "application/pdf"]}
            maxSizeKB={5000}
            uploadToServer={true}
            multiple={true}
            maxFiles={2}
            disabled={passenger.cprProcessing}
            initialFiles={passenger.cprFiles || []}
          />

          {/* CPR Processing Status */}
          {passenger.cprProcessing && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-sm text-blue-700">
                  {translate(
                    "Processing CPR data...",
                    "جاري معالجة بيانات الهوية..."
                  )}
                </p>
              </div>
              <p className="text-xs text-blue-600 mt-1">
                {translate(
                  "Please wait while we extract your information...",
                  "يرجى الانتظار بينما نستخرج معلوماتك..."
                )}
              </p>
            </div>
          )}

          {/* Show inline error for CPR upload */}
          {getCprUploadError(index) && (
            <div className="mt-2 text-red-600 text-sm">
              {getCprUploadError(index)}
            </div>
          )}
        </div>

        {/* CPR Number */}
        <div className="mb-3">
          <Input
            id={`cprNumber_${passenger.id}`}
            label={translate("CPR Number", "رقم الهوية الوطنية")}
            type="text"
            value={passenger.cprNumber}
            handleChange={(e) => {
              const value = e.target.value.replace(/\D/g, ""); // Only allow digits
              handleInputChange(index, "cprNumber", value);
            }}
            error={getFieldError(index, "cprNumber")}
            disabled={passenger.cprProcessing}
            maxLength={9}
          />
        </div>

        {/* Full Name */}
        <div className="mb-3">
          <Input
            id={`fullName_${passenger.id}`}
            label={translate("Full Name", "الاسم الكامل")}
            type="text"
            value={passenger.fullName}
            handleChange={(e) =>
              handleInputChange(index, "fullName", e.target.value)
            }
            error={getFieldError(index, "fullName")}
            disabled={passenger.cprProcessing}
          />
        </div>

        {/* Email Address */}
        <div className="mb-3">
          <Input
            id={`emailAddress_${passenger.id}`}
            label={translate("Email Address", "عنوان البريد الإلكتروني")}
            type="email"
            value={passenger.emailAddress}
            handleChange={(e) =>
              handleInputChange(index, "emailAddress", e.target.value)
            }
            error={getFieldError(index, "emailAddress")}
          />
        </div>

        {/* Mobile Number */}
        <div className="mb-3">
          <TelInput
            name="mobileNumber"
            value={passenger.mobileNumber}
            error={getFieldError(index, "mobileNumber")}
            onChange={(e) =>
              handleInputChange(index, e.target.name, e.target.value)
            }
            label={translate("Mobile Number", "رقم الهاتف المحمول")}
          />
        </div>

        {/* Nationality */}
        <div className="mb-3">
          <SelectInput
            name="nationality"
            formData={passenger}
            label={translate("Nationality", "الجنسية")}
            options_data={nationalityOptions || []}
            error={getFieldError(index, "nationality")}
            handleChange={(e) =>
              !passenger.cprProcessing &&
              handleInputChange(index, e.target.name, e.target.value)
            }
            default_placeholder={translate(
              "Please select an option.",
              "يرجى اختيار خيار."
            )}
            disabled={passenger.cprProcessing || nationalitiesLoading}
          />
        </div>

        {/* Gender */}
        <div
          className={`mb-3 ${
            passenger.cprProcessing ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <FormTabs
            label={translate("Gender", "الجنس")}
            tabs={genderTabs}
            onTabClick={(type) =>
              !passenger.cprProcessing &&
              handleInputChange(index, "gender", type)
            }
            activeTab={passenger.gender}
            error={getFieldError(index, "gender")}
          />
        </div>

        {/* Date of Birth */}
        <div className="date_of_birth mb-3">
          <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
            {translate("Date of Birth", "تاريخ الميلاد")}
          </label>

          {/* Mobile: native date picker (visible <=767px) */}
          <div className="hidden max767:block">
            <SingleDatePicker
              value={passenger.dateOfBirth?.fullDate || ""}
              onChange={(dateString) => handleMobileDatePickerSelect(index, dateString)}
              placeholder={translate("Select Date", "اختر التاريخ")}
              error={
                getFieldError(index, "dateOfBirth.day") ||
                getFieldError(index, "dateOfBirth.month") ||
                getFieldError(index, "dateOfBirth.year")
              }
              useNativeMobile={true}
              disabled={passenger.cprProcessing}
            />
          </div>

          {/* Desktop: select dropdowns (visible >767px) */}
          <div className="max767:!hidden">
            <div className="flex gap-6 self-stretch">
              <SelectInput
                name="day"
              options_data={daysOptions}
              formData={{ day: passenger.dateOfBirth.day || "" }}
              default_placeholder={translate("Day", "يوم")}
              handleChange={(e) => {
                const day = e.target.value;
                const month = passenger.dateOfBirth.month;
                const year = passenger.dateOfBirth.year;
                const fullDate =
                  day && month && year
                    ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                        2,
                        "0"
                      )}`
                    : "";
                handleInputChange(index, "dateOfBirth", {
                  day,
                  month,
                  year,
                  fullDate,
                });
              }}
              disabled={passenger.cprProcessing}
              maxMenuHeight={185}
            />

            <SelectInput
              name="month"
              options_data={monthsOptions}
              formData={{ month: passenger.dateOfBirth.month || "" }}
              default_placeholder={translate("Month", "شهر")}
              handleChange={(e) => {
                const month = e.target.value;
                const day = passenger.dateOfBirth.day;
                const year = passenger.dateOfBirth.year;
                const fullDate =
                  day && month && year
                    ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                        2,
                        "0"
                      )}`
                    : "";
                handleInputChange(index, "dateOfBirth", {
                  day,
                  month,
                  year,
                  fullDate,
                });
              }}
              disabled={passenger.cprProcessing}
              maxMenuHeight={185}
            />

            <SelectInput
              name="year"
              options_data={yearsOptions}
              formData={{ year: passenger.dateOfBirth.year || "" }}
              default_placeholder={translate("Year", "سنة")}
              handleChange={(e) => {
                const year = e.target.value;
                const day = passenger.dateOfBirth.day;
                const month = passenger.dateOfBirth.month;
                const fullDate =
                  day && month && year
                    ? `${year}-${month.padStart(2, "0")}-${day.padStart(
                        2,
                        "0"
                      )}`
                    : "";
                handleInputChange(index, "dateOfBirth", {
                  day,
                  month,
                  year,
                  fullDate,
                });
              }}
              disabled={passenger.cprProcessing}
              maxMenuHeight={185}
            />
            </div>
            {(getFieldError(index, "dateOfBirth.day") ||
              getFieldError(index, "dateOfBirth.month") ||
              getFieldError(index, "dateOfBirth.year")) && (
              <div className="mt-2 text-red-600 text-sm">
                {getFieldError(index, "dateOfBirth.day") ||
                  getFieldError(index, "dateOfBirth.month") ||
                  getFieldError(index, "dateOfBirth.year")}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <PersonalCyberJourneyLayout is_hide_back_btn={false}>
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_disabled={quoteLoading || (policyFor === "someone_else" && !hasCalledQuoteApi)}
          btn_label={translate("Continue", "التالي")}
          isLoading={quoteLoading}
          show_summary={
            !quoteLoading &&
            personalCyberQuote?.totalPremium &&
            personalCyberQuote.totalPremium > 0 &&
            !(policyFor === "someone_else" && !hasCalledQuoteApi)
          }
          price={personalCyberQuote?.totalPremium || 0}
          onShowSummary={() => setShowSummaryModal(true)}
        >
          <div className="flex flex-col items-center p-4 max767:p-0 mx-auto pt-0 py-4 pb-28">
            <div className="w-full max-w-[500px]">
              <div className="contactdetailstitle max767:ml-[0px] max767:mt-0 max767:w-full max767:pb-0 max767:p-[22px] max767:pt-0">
                <h1 className="text-[#1A2024] text-center text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] max767:text-left mb-2 max767:rtl:text-right">
                  {translate("Your Information", "بياناتك")}
                </h1>
                <p className="text-[#1A2024] text-center text-base not-italic font-normal leading-6 tracking-[-0.16px] mb-6 max767:text-left max767:rtl:text-right">
                  {translate(
                    "Fill in your information, and we'll take care of the rest.",
                    "يرجى إدخال المعلومات المطلوبة أدناه."
                  )}
                </p>
              </div>

              {renderLoggedInSection()}

              {/* Passengers Forms */}
              {passengers.map((passenger, index) => {
                // Skip rendering passenger 1 form when "For Myself" is selected for logged-in users
                if (!isGuest && policyFor === "myself" && index === 0) {
                  return null;
                }
                return renderPassengerForm(passenger, index);
              })}
            </div>
          </div>
        </BottomStickyBtnLayout>
      </PersonalCyberJourneyLayout>

      {/* Travel Summary Modal (Reused) */}
      <TravelSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        selectedPlan={{
          // Add quote data properties that TravelSummaryModal expects
          premium: personalCyberQuote?.premium || 0,
          tax: personalCyberQuote?.tax || 0,
          totalPremium: personalCyberQuote?.totalPremium || 0,
          quoteNumber:
            personalCyberQuote?.quoteNumber || personalCyberQuote?.policyNumber,
          apiData: personalCyberQuote,
          isRealApiData: true,
        }}
      />
    </div>
  );
};

export default PlanSelection;
